import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Package,
  DollarSign,
  ShoppingCart,
  Users,
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

const salesData = [
  { day: 'Lun', ventas: 12500, utilidad: 3200 },
  { day: 'Mar', ventas: 15800, utilidad: 4100 },
  { day: 'Mié', ventas: 14200, utilidad: 3800 },
  { day: 'Jue', ventas: 18900, utilidad: 5200 },
  { day: 'Vie', ventas: 22100, utilidad: 6500 },
  { day: 'Sáb', ventas: 19800, utilidad: 5800 },
  { day: 'Dom', ventas: 16400, utilidad: 4600 },
]

const branchComparison = [
  { sucursal: 'Centro', ventas: 45200, utilidad: 12800 },
  { sucursal: 'Norte', ventas: 38900, utilidad: 10200 },
  { sucursal: 'Sur', ventas: 41500, utilidad: 11600 },
]

const kpis = [
  {
    title: 'Ventas Hoy',
    value: '$22,450',
    change: '+12.5%',
    trend: 'up',
    icon: DollarSign,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    title: 'Utilidad del Día',
    value: '$6,890',
    change: '+8.2%',
    trend: 'up',
    icon: TrendingUp,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
  },
  {
    title: 'Productos Vendidos',
    value: '342',
    change: '-3.1%',
    trend: 'down',
    icon: ShoppingCart,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    title: 'Clientes Atendidos',
    value: '127',
    change: '+5.8%',
    trend: 'up',
    icon: Users,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
]

const criticalAlerts = [
  {
    id: 1,
    type: 'stock',
    product: 'Ibuprofeno 400mg',
    branch: 'Sucursal Centro',
    stock: 5,
    severity: 'high',
  },
  {
    id: 2,
    type: 'stock',
    product: 'Amoxicilina 500mg',
    branch: 'Sucursal Norte',
    stock: 12,
    severity: 'medium',
  },
  {
    id: 3,
    type: 'expiry',
    product: 'Loratadina 10mg',
    branch: 'Sucursal Sur',
    days: 15,
    severity: 'medium',
  },
  {
    id: 4,
    type: 'noMovement',
    product: 'Complejo B',
    branch: 'Sucursal Centro',
    days: 45,
    severity: 'low',
  },
]

const recentActivity = [
  {
    id: 1,
    type: 'sale',
    description: 'Venta #VT-1245 - $450.00',
    user: 'María López',
    time: 'Hace 5 min',
  },
  {
    id: 2,
    type: 'adjustment',
    description: 'Ajuste de inventario - Paracetamol 500mg',
    user: 'Carlos Ruiz',
    time: 'Hace 12 min',
  },
  {
    id: 3,
    type: 'purchase',
    description: 'Orden de compra #OC-892 recibida',
    user: 'Sistema',
    time: 'Hace 25 min',
  },
  {
    id: 4,
    type: 'sale',
    description: 'Venta #VT-1244 - $280.00',
    user: 'Ana Torres',
    time: 'Hace 32 min',
  },
]

export function Dashboard() {
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Panel General</h1>
        <p className="mt-1 text-sm text-slate-600">
          Lunes, 27 de Abril de 2026
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.title}
            className="rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-600">{kpi.title}</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">
                  {kpi.value}
                </p>
                <div className="mt-2 flex items-center gap-1">
                  {kpi.trend === 'up' ? (
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-600" />
                  )}
                  <span
                    className={`text-sm ${
                      kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {kpi.change}
                  </span>
                  <span className="ml-1 text-xs text-slate-500">vs ayer</span>
                </div>
              </div>
              <div className={`rounded-lg p-3 ${kpi.bgColor}`}>
                <kpi.icon className={`h-6 w-6 ${kpi.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Sales Trend */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-4 font-semibold text-slate-900">
            Tendencia de Ventas - Última Semana
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
              <Line
                type="monotone"
                dataKey="ventas"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="utilidad"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Branch Comparison */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-4 font-semibold text-slate-900">
            Comparativo entre Sucursales
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={branchComparison}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="sucursal" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="ventas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="utilidad" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts and Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Critical Alerts */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-slate-900">Alertas Críticas</h3>
          </div>
          <div className="space-y-3">
            {criticalAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`rounded-lg border p-3 ${
                  alert.severity === 'high'
                    ? 'border-red-200 bg-red-50'
                    : alert.severity === 'medium'
                      ? 'border-yellow-200 bg-yellow-50'
                      : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">
                      {alert.product}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {alert.branch}
                    </p>
                    {alert.type === 'stock' && (
                      <p className="mt-1 text-xs text-slate-600">
                        Stock actual:{' '}
                        <span className="font-medium">
                          {alert.stock} unidades
                        </span>
                      </p>
                    )}
                    {alert.type === 'expiry' && (
                      <p className="mt-1 text-xs text-slate-600">
                        Vence en:{' '}
                        <span className="font-medium">{alert.days} días</span>
                      </p>
                    )}
                    {alert.type === 'noMovement' && (
                      <p className="mt-1 text-xs text-slate-600">
                        Sin rotación:{' '}
                        <span className="font-medium">{alert.days} días</span>
                      </p>
                    )}
                  </div>
                  <Package
                    className={`h-4 w-4 ${
                      alert.severity === 'high'
                        ? 'text-red-600'
                        : alert.severity === 'medium'
                          ? 'text-yellow-600'
                          : 'text-slate-600'
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="mb-4 font-semibold text-slate-900">
            Actividad Reciente
          </h3>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 rounded-full bg-blue-600" />
                <div className="flex-1">
                  <p className="text-sm text-slate-900">
                    {activity.description}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    {activity.user} • {activity.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
